import moment from 'moment';
import React from 'react';
import {
  createPaginationContainer,
  Environment,
  graphql,
  RefetchOptions,
} from 'react-relay';
import styled from 'styled-components';
import Spacer from '../../components/Spacer';
import { PageTitle } from '../../components/typography/Typography';
import { Activity_account } from './__generated__/Activity_account.graphql';

const Container = styled.div`
  flex: 1;
`;

const DateTitle = styled.div`
  font-size: 19px;
  font-weight: 700;
  margin: 29px 0px;
`;

const ActivityItem = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 11px;
`;

const ActivityImage = styled.div<{ url: string }>`
  width: 40px;
  height: 40px;
  background-image: url(${(props) => props.url});
  border-radius: 10px;
  background-size: contain;
`;

const ActivityTitle = styled.div`
  font-size: 15px;
  font-weight: 500;
`;

const ActivityDescription = styled.div`
  font-size: 13px;
  color: #9092c1;
`;

const ActivityText = styled.div``;

const ActivityDate = styled.div`
  margin-left: auto;
  font-size: 14px;
  font-weight: 300;
`;

const NothingHere = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const NothingTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
`;

const NothingSubtitle = styled.div`
  font-size: 13px;
  color: #9092c1;
`;

const NothingImage = styled.img``;

type Props = {
  account: Activity_account;
  relay: {
    environment: Environment;
    hasMore: () => boolean;
    isLoading: () => boolean;
    loadMore: (
      pageSize: number,
      callback: (error: Error) => void,
      options?: RefetchOptions
    ) => any;
  };
};

const eventText: any = {
  SUBSCRIBED: 'Subscribed to API',
  PAYMENT_ADDED: 'A payment method was added to your account',
  PAYMENT_UPDATED: 'Your payment method was updated',
  UNSUBSCRIBED: 'Unsubscribed from API',
};

function Activity({ account, relay }: Props) {
  // const { hasMore, isLoading, loadMore } = relay;

  return (
    <Container>
      <Spacer spacing={4} vertical />
      <PageTitle>Activity</PageTitle>
      <Spacer vertical spacing={2} />
      {account.activity.edges?.length === 0 && (
        <>
          <Spacer vertical spacing={3} />
          <NothingHere>
            <NothingTitle>No activity yet</NothingTitle>
            <Spacer vertical spacing={1} />
            <NothingSubtitle>
              Your account activity will show here
            </NothingSubtitle>
            <Spacer vertical spacing={4} />
            <NothingImage src={'/images/noactivity.svg'} />
          </NothingHere>
        </>
      )}
      {(account.activity.edges?.length ?? 0) > 0 &&
        account?.activity?.edges?.map((activity, index, arr) => {
          let imageUrl = '';

          switch (activity?.node?.eventType) {
            case 'SUBSCRIBED':
              imageUrl = activity?.node?.subscription?.service.imageURL ?? '';
              break;
            case 'UNSUBSCRIBED':
              imageUrl = activity?.node?.service?.imageURL ?? '';
              break;
            case 'PAYMENT_ADDED':
              imageUrl = '/images/activityNewMethod.svg';
              break;
            case 'PAYMENT_UPDATED':
              imageUrl = '/images/activityNewMethod.svg';
              break;
          }
          return (
            <>
              {!arr[index - 1] &&
                new Date(activity?.node?.createdAt ?? '').getMonth() !==
                  new Date(
                    arr[index - 1]?.node?.createdAt ?? ''
                  ).getMonth() && (
                  <DateTitle>
                    {moment(activity?.node?.createdAt).format('MMMM')}
                  </DateTitle>
                )}
              <ActivityItem>
                <ActivityImage url={imageUrl} />
                <Spacer horizontal spacing={2} />
                <ActivityText>
                  <ActivityTitle>
                    {activity?.node?.subscription?.service.name ??
                      activity?.node?.service?.name ??
                      activity?.node?.description ??
                      '-'}
                  </ActivityTitle>
                  <ActivityDescription>
                    {eventText[activity?.node?.eventType ?? '']}
                  </ActivityDescription>
                </ActivityText>
                <ActivityDate>
                  {moment(activity?.node?.createdAt).fromNow()}
                </ActivityDate>
              </ActivityItem>
            </>
          );
        })}
    </Container>
  );
}

export default createPaginationContainer(
  Activity,
  {
    account: graphql`
      fragment Activity_account on Account
        @argumentDefinitions(
          count: { type: "Int", defaultValue: 10 }
          cursor: { type: "String" }
        ) {
        activity(first: $count, after: $cursor)
          @connection(key: "Activity_activity") {
          edges {
            node {
              id
              createdAt
              description
              eventType
              service {
                name
                imageURL
              }
              subscription {
                service {
                  name
                  imageURL
                }
              }
            }
          }
        }
      }
    `,
  },
  {
    getConnectionFromProps(props: any) {
      return props.account && props.account.subscriptions;
    },
    // This is also the default implementation of `getFragmentVariables` if it isn't provided.
    getFragmentVariables(prevVars, totalCount) {
      return {
        ...prevVars,
        count: totalCount,
      };
    },
    getVariables(props, { count, cursor }, fragmentVariables) {
      return {
        count,
        cursor,
        // userID isn't specified as an @argument for the fragment, but it should be a variable available for the fragment under the query root.
        accountID: fragmentVariables.accountID,
      };
    },
    query: graphql`
      # Pagination query to be fetched upon calling 'loadMore'.
      # Notice that we re-use our fragment, and the shape of this query matches our fragment spec.
      query ActivityPaginationQuery(
        $count: Int!
        $cursor: String
        $accountID: ID!
      ) {
        account: node(id: $accountID) {
          ...Activity_account @arguments(count: $count, cursor: $cursor)
        }
      }
    `,
  }
);
